import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { getAuditExportUrl, listAuditLogs } from "../api/endpoints/audit"
import { PageSection, StatusPill } from "../components/ui"
import { formatDateTime } from "../utils/formatting"

export function AuditLogsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(() => searchParams.get("search") ?? "")
  const [severity, setSeverity] = useState(() => searchParams.get("severity") ?? "")

  useEffect(() => {
    const routeSearch = searchParams.get("search") ?? ""
    if (routeSearch !== search) {
      setSearch(routeSearch)
    }

    const routeSeverity = searchParams.get("severity") ?? ""
    if (routeSeverity !== severity) {
      setSeverity(routeSeverity)
    }
  }, [search, searchParams, severity])

  function updateRouteState(next: {
    search?: string | null
    severity?: string | null
  }) {
    const params = new URLSearchParams(searchParams)

    if (next.search !== undefined) {
      if (next.search) {
        params.set("search", next.search)
      } else {
        params.delete("search")
      }
    }

    if (next.severity !== undefined) {
      if (next.severity) {
        params.set("severity", next.severity)
      } else {
        params.delete("severity")
      }
    }

    setSearchParams(params)
  }

  const auditQuery = useQuery({
    queryKey: ["audit-logs", search, severity],
    queryFn: () =>
      listAuditLogs({
        ...(search ? { search } : {}),
        ...(severity ? { severity } : {}),
        per_page: "100",
      }),
  })

  const rows = auditQuery.data?.data ?? []

  return (
    <div className="flex flex-col gap-5">
      <PageSection
        actions={
          <div className="flex flex-wrap items-center gap-3">
            <input
              className="w-64 px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-slate-50 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              onChange={(event) => {
                const nextSearch = event.target.value
                setSearch(nextSearch)
                updateRouteState({ search: nextSearch || null })
              }}
              placeholder="Search action, actor, entity, or IP"
              value={search}
            />
            <select
              className="px-3 py-1.5 border border-slate-200 rounded-md text-sm bg-white"
              onChange={(event) => {
                setSeverity(event.target.value)
                updateRouteState({ severity: event.target.value || null })
              }}
              value={severity}
            >
              <option value="">All severities</option>
              <option value="NORMAL">Normal</option>
              <option value="ELEVATED">Elevated</option>
              <option value="HIGH_RISK">High risk</option>
            </select>
            <a className="text-slate-600 hover:bg-slate-100 px-4 py-2 rounded-md text-sm transition-colors" href={getAuditExportUrl({ search, severity })}>
              Export CSV
            </a>
          </div>
        }
        title="Audit logs"
      >
        <table className="w-full text-left text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500 font-bold uppercase border-b border-slate-100">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Actor</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Resource</th>
              <th className="px-4 py-3">Severity</th>
              <th className="px-4 py-3">Request</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row: any) => (
              <tr key={row.auditId}>
                <td className="px-4 py-3">{formatDateTime(row.timestamp)}</td>
                <td className="px-4 py-3">
                  <strong>{row.actorName ?? row.actorId}</strong>
                  <div className="text-xs text-slate-500">{row.actorRole}</div>
                </td>
                <td className="px-4 py-3">
                  <strong>{row.actionType}</strong>
                  <div className="text-xs text-slate-500">{row.description ?? "-"}</div>
                </td>
                <td className="px-4 py-3">
                  {row.resourceType} · {row.resourceId ?? "-"}
                </td>
                <td className="px-4 py-3">
                  <StatusPill
                    tone={
                      row.severity === "HIGH_RISK"
                        ? "danger"
                        : row.severity === "ELEVATED"
                          ? "warning"
                          : "neutral"
                    }
                  >
                    {row.severity ?? "NORMAL"}
                  </StatusPill>
                </td>
                <td className="px-4 py-3">
                  <div>{row.requestMethod ?? "-"}</div>
                  <div className="text-xs text-slate-500">{row.endpoint ?? "-"}</div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </PageSection>
    </div>
  )
}
